/* Machine translation of text people typed (chat, tasks, notes…). Batches requests to POST /api/translate,
   caches every phrase in memory + localStorage so the same line is never fetched twice on this device. */
import api from "@/lib/api";

const LS = "kingly.mt.v1";
const MAX_LS = 3000;
const MEM = new Map();      // `${target}\0${text}` → translation (equals text when nothing to translate)
const FAILED = new Map();   // key → timestamp of last failure (retry after 60 s)
const pending = new Map();  // key → { text, target, cbs: [] , sent }
let timer = null, inflight = 0, persistTimer = null, enabled = false;

try { for (const [k, v] of JSON.parse(localStorage.getItem(LS) || "[]")) MEM.set(k, v); } catch { /* ignore */ }

const key = (target, text) => `${target}\u0000${text}`;
export const setMTEnabled = (on) => { enabled = !!on; };
export const isMTEnabled = () => enabled;

const ACCENTS = /[ñáéíóúü¿¡]/i;
const STRONG = /\b(gracias|listo|lista|listos|hecho|hola|claro|vale|bueno|buenos|buenas|hoy|ahora|también|necesito|necesita|tengo|tiene|puedo|puede|está|están|porque|cuándo|dónde|ya|después|antes|entonces|favor|caballo|caballos|yegua|potro|heno|viruta|agua|comida|medicina|trenzar|trenzas|bandas|pista|clase|clases|dueño|dueña|entrenador|entrenadora|herrador|veterinario|mañana|noche|tarde|temprano|todavía|nada|algo|alguien|conmigo|contigo|quiero|quiere|hacer|hice|hizo|vamos|voy|fue|era|estoy|estamos|somos|son)\b/gi;
const WEAK = /\b(el|la|los|las|de|del|que|para|con|por|una|un|y|en|es|muy|bien|todo|todos|pero|como|su|sus|mi|mis|le|lo|se|al|este|esta|esto|ese|esa|eso|aquí|allí|más|menos|sin|sobre|hasta|desde|entre|donde|cuando|porque|si|o)\b/gi;

/** Cheap guess: accents, one Spanish-only word, or two function words. */
export function likelySpanish(s) {
  if (!s) return false;
  if (ACCENTS.test(s)) return true;
  if ((s.match(STRONG) || []).length >= 1) return true;
  return (s.match(WEAK) || []).length >= 2;
}

export function translatable(s) {
  const k = (s || "").trim();
  return k.length >= 2 && k.length <= 600 && /[A-Za-zÀ-ÿ]{2,}/.test(k) && !/^[\w.+-]+@[\w-]+\.[\w.]+$/.test(k) && !/^https?:\/\//i.test(k);
}

/** Does this text need machine translation to reach `target`? */
export function wants(text, target) {
  if (!enabled || !translatable(text)) return false;
  const es = likelySpanish(text);
  return target === "es" ? !es : es;
}

export const cached = (text, target) => MEM.get(key(target, (text || "").trim()));

/** Ask for a translation; `cb(translation)` fires from cache synchronously or after the batch returns. */
export function request(text, target, cb) {
  const t = text.trim();
  const k = key(target, t);
  if (MEM.has(k)) { cb(MEM.get(k)); return; }
  const f = FAILED.get(k);
  if (f && Date.now() - f < 60000) return;
  let p = pending.get(k);
  if (!p) { p = { text: t, target, cbs: [], sent: false }; pending.set(k, p); }
  p.cbs.push(cb);
  schedule();
}

function schedule() { if (!timer) timer = setTimeout(flush, 250); }

function persist() {
  if (persistTimer) return;
  persistTimer = setTimeout(() => {
    persistTimer = null;
    try { localStorage.setItem(LS, JSON.stringify([...MEM.entries()].slice(-MAX_LS))); } catch { /* quota */ }
  }, 1000);
}

async function flush() {
  timer = null;
  if (inflight >= 2) { schedule(); return; }
  const first = [...pending.values()].find((p) => !p.sent);
  if (!first) return;
  const batch = [...pending.values()].filter((p) => !p.sent && p.target === first.target).slice(0, 60);
  batch.forEach((p) => { p.sent = true; });
  inflight += 1;
  try {
    const { data } = await api.post("/translate", { texts: batch.map((p) => p.text), target: first.target });
    for (const p of batch) {
      const k = key(p.target, p.text); pending.delete(k);
      const out = data?.translations?.[p.text];
      if (typeof out === "string") { MEM.set(k, out); p.cbs.forEach((cb) => { try { cb(out); } catch { /* node gone */ } }); }
      else FAILED.set(k, Date.now());
    }
    persist();
  } catch {
    for (const p of batch) { pending.delete(key(p.target, p.text)); FAILED.set(key(p.target, p.text), Date.now()); }
  } finally {
    inflight -= 1;
    if ([...pending.values()].some((p) => !p.sent)) schedule();
  }
}
